import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { authRedirectUrl, friendlyAuthError } from "@/lib/auth";
import { AuthInput, AuthLabel, AuthShell, ErrorMessage, PrimaryButton } from "@/components/auth/AuthShell";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError("Please enter a valid email.");
    setError(null);
    setSubmitting(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: authRedirectUrl("/reset-password"),
    });
    setSubmitting(false);
    // Same confirmation whether or not the email exists, so accounts can't be discovered here.
    if (resetError && !/not found/i.test(resetError.message)) return setError(friendlyAuthError(resetError.message));
    setSent(true);
  }

  return (
    <AuthShell title="Reset password" subtitle="We'll email you a link to choose a new password.">
      {sent ? (
        <p className="text-sm leading-relaxed text-white/80">
          If an account exists for <strong className="text-white">{email.trim()}</strong>, a reset link is on its way. Check
          your inbox and spam folder.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <AuthLabel htmlFor="email">Email</AuthLabel>
            <AuthInput id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
          </div>
          <ErrorMessage message={error} />
          <PrimaryButton type="submit" disabled={submitting}>
            {submitting ? "Sending…" : "Send reset link"}
          </PrimaryButton>
        </form>
      )}
      <p className="mt-5 text-center text-sm text-white/60">
        <Link to="/login" className="font-semibold text-white underline decoration-a1-red underline-offset-4">
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
}
